'use client';

/**
 * ProjectCardSection — schema-driven hero project card.
 *
 * Wraps the existing `<ProjectCard />` (the RAG-coloured project hero
 * card) so it can be declared by `EXPLORER_SCHEMA` rather than
 * inlined inside a bespoke explorer. Reads `projectContext` from
 * `<ExplorerSearchProvider />`; renders nothing when no project is
 * focused.
 *
 * Today the only mode that pushes a project into the search context
 * is obeya simulations (via `ki_ask` returning a focused project) —
 * agents mode is wired the same way. Future modes get the same UI
 * for free by writing a provider that sets `projectContext`.
 *
 * @see klyde_edge/ui_config/entity_schema.py — ExplorerSectionType.PROJECT_CARD
 *
 * @component_role: Section
 * @component_type: Section Renderer
 * @domain: Frontend / Explorer
 * @layer: presentation
 */

import { ProjectCard, type ProjectCardData } from '../project-card';
import type { ExplorerSectionConfig, LabelsDict, RAGStatus } from '@/lib/api/types';
import { useExplorerSearch } from './explorer-search-context';

interface ProjectCardSectionProps {
  config: ExplorerSectionConfig;
  labels: LabelsDict;
}

export function ProjectCardSection(_props: ProjectCardSectionProps) {
  const search = useExplorerSearch();
  if (!search?.projectContext) return null;

  const ctx = search.projectContext;
  const data: ProjectCardData = {
    semantic_id: ctx.semantic_id,
    name: ctx.name,
    rag_status: (ctx.rag_status ?? 'unknown') as RAGStatus,
    rag_label: ctx.rag_label ?? '',
    rfq_count: ctx.rfq_count ?? 0,
    po_count: ctx.po_count ?? 0,
  };

  return (
    <div className="px-2 py-2 border-b border-border/50">
      <ProjectCard project={data} onClear={search.clearProject} compact />
    </div>
  );
}
